import type RAPIER from '@dimforge/rapier3d-compat';
import { CELL, GROUND } from '../core/config';
import { terrainHeight, WATER_LEVEL } from '../world/terrain';
import { cellMass, physicalCenter } from '../destruction/structure';
import type { PhysicsCity, Cluster } from '../physics/world';
import type { City } from '../world/city';

export { WATER_LEVEL } from '../world/terrain';
export interface FloatingTarget {
  id: string;
  body: () => RAPIER.RigidBody | undefined;
  colliders: () => RAPIER.Collider[];
  halfHeight: number;
  centerY?: number;
  halfWidth?: number;
  halfLength?: number;
  centerX?: number;
  centerZ?: number;
  /** Fraction of the hull or wood volume submerged at rest. */
  draft?: number;
}
interface Entry {
  target: FloatingTarget;
  body?: RAPIER.RigidBody;
  wet: boolean;
  wood: boolean;
  cells?: number;
}
const LIMIT = 512;
const clamp = (n: number, min: number, max: number): number => Math.max(min, Math.min(max, n));

/** Bounded displacement and drag above the shared physical riverbed and coast.
 * Collision groups are never changed: banks, debris and structures remain solid. */
export class WaterSystem {
  private entries = new Map<string, Entry>();
  private rejected = new WeakMap<Cluster, number>();
  private world?: RAPIER.World;
  private age = 1;
  private restorations = 0;
  constructor(
    readonly physics: PhysicsCity,
    readonly city: City,
  ) {
    this.world = physics.world;
  }
  register(target: FloatingTarget): void {
    this.unregister(target.id);
    if (this.entries.size >= LIMIT) return;
    this.entries.set(target.id, { target, wet: false, wood: false });
  }
  unregister(id: string): void {
    const entry = this.entries.get(id);
    if (entry && this.world === this.physics.world) this.restore(entry);
    this.entries.delete(id);
  }
  clear(): void {
    if (this.world === this.physics.world) for (const entry of this.entries.values()) this.restore(entry);
    this.entries.clear();
    this.rejected = new WeakMap();
    this.world = this.physics.world;
    this.age = 1;
    this.restorations = 0;
  }
  /** End caps are finite; nearest-segment distance follows the actual curved river. */
  private waterAt(x: number, z: number, margin: number): { x: number; z: number } | undefined {
    const terrain = this.city.terrain,
      river = this.city.river;
    if (!terrain) return;
    for (const [dx, dz] of [
      [0, 0],
      [margin, 0],
      [-margin, 0],
      [0, margin],
      [0, -margin],
    ])
      if ((terrainHeight(terrain, x + dx, z + dz) ?? GROUND) >= WATER_LEVEL) return;
    if (!river || river.points.length < 2) return { x: 0.25, z: 0 };
    const points = river.points;
    let best = Infinity,
      current: { x: number; z: number } | undefined;
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i],
        b = points[i + 1],
        dx = b.x - a.x,
        dz = b.z - a.z;
      const length = Math.hypot(dx, dz);
      if (!length) continue;
      const t = clamp(((x - a.x) * dx + (z - a.z) * dz) / (length * length), 0, 1);
      const d = Math.hypot(x - a.x - dx * t, z - a.z - dz * t);
      if (d < best) {
        best = d;
        current = { x: dx / length, z: dz / length };
      }
    }
    return best < river.width * 0.5 + 4 ? current : { x: 0.25, z: 0 };
  }
  private discover(): void {
    const live = new Set<string>();
    for (const c of this.physics.clusters.values()) {
      if (c.anchored) continue;
      const id = `wood:${c.id}`;
      live.add(id);
      const prior = this.entries.get(id);
      // Supported fixed rubble cannot travel into water. Existing wet targets
      // remain registered; a later impact makes a land fragment dynamic again.
      if (c.settled && !prior) continue;
      if (prior && !prior.wet && !this.waterAt(c.body.translation().x, c.body.translation().z, 0)) {
        this.unregister(id);
        continue;
      }
      if (prior?.body === c.body && prior.cells === c.cells.size) continue;
      if (!prior && this.rejected.get(c) === c.cells.size) continue;
      if (
        !prior &&
        (this.entries.size >= LIMIT || !this.waterAt(c.body.translation().x, c.body.translation().z, 0))
      )
        continue;
      if (prior) this.unregister(id);
      const target = this.woodTarget(c);
      if (target)
        this.entries.set(id, {
          target,
          body: c.body,
          cells: c.cells.size,
          wet: false,
          wood: true,
        });
      else this.rejected.set(c, c.cells.size);
    }
    for (const [id, entry] of this.entries) if (entry.wood && !live.has(id)) this.unregister(id);
  }
  private woodTarget(cluster: Cluster): FloatingTarget | undefined {
    let mass = 0,
      wood = 0,
      lowX = Infinity,
      lowY = Infinity,
      lowZ = Infinity;
    let highX = -Infinity,
      highY = -Infinity,
      highZ = -Infinity;
    for (const cell of cluster.cells.values()) {
      const m = cellMass(cell),
        p = physicalCenter(cell),
        b = cell.bounds;
      mass += m;
      if (cell.material === 'wood' || cell.material === 'foliage') wood += m;
      lowX = Math.min(lowX, p.x - (CELL * (b?.w ?? 1)) / 2);
      highX = Math.max(highX, p.x + (CELL * (b?.w ?? 1)) / 2);
      lowY = Math.min(lowY, p.y - (CELL * (b?.h ?? 1)) / 2);
      highY = Math.max(highY, p.y + (CELL * (b?.h ?? 1)) / 2);
      lowZ = Math.min(lowZ, p.z - (CELL * (b?.d ?? 1)) / 2);
      highZ = Math.max(highZ, p.z + (CELL * (b?.d ?? 1)) / 2);
    }
    if (!mass || wood / mass < 0.7) return;
    return {
      id: `wood:${cluster.id}`,
      body: () => this.physics.clusters.get(cluster.id)?.body,
      colliders: () => this.physics.clusters.get(cluster.id)?.colliders ?? [],
      halfWidth: (highX - lowX) / 2,
      halfHeight: (highY - lowY) / 2,
      halfLength: (highZ - lowZ) / 2,
      centerX: (highX + lowX) / 2 - cluster.origin.x,
      centerY: (highY + lowY) / 2 - cluster.origin.y,
      centerZ: (highZ + lowZ) / 2 - cluster.origin.z,
      draft: 0.58,
    };
  }
  private restore(entry: Entry): void {
    if (entry.wet) this.restorations++;
    entry.wet = false;
  }
  step(dt: number): void {
    if (this.world !== this.physics.world) {
      this.entries.clear();
      this.rejected = new WeakMap();
      this.world = this.physics.world;
      this.age = 1;
    }
    dt = clamp(dt, 0, 1 / 30);
    this.age += dt;
    if (this.age >= 0.2) {
      this.age = 0;
      this.discover();
    }
    for (const entry of this.entries.values()) {
      const body = entry.target.body();
      if (!body?.isValid()) {
        this.restore(entry);
        continue;
      }
      if (entry.body && entry.body !== body) this.restore(entry);
      entry.body = body;
      const t = entry.target,
        p = body.translation(),
        q = body.rotation();
      // Rotated bounding extent lets a tipped hull or log use its actual vertical size.
      const xx = 1 - 2 * (q.y * q.y + q.z * q.z),
        xy = 2 * (q.x * q.y - q.z * q.w),
        xz = 2 * (q.x * q.z + q.y * q.w);
      const yx = 2 * (q.x * q.y + q.z * q.w),
        yy = 1 - 2 * (q.x * q.x + q.z * q.z),
        yz = 2 * (q.y * q.z - q.x * q.w);
      const zx = 2 * (q.x * q.z - q.y * q.w),
        zy = 2 * (q.y * q.z + q.x * q.w),
        zz = 1 - 2 * (q.x * q.x + q.y * q.y);
      const cx = t.centerX ?? 0,
        cy = t.centerY ?? 0,
        cz = t.centerZ ?? 0;
      const center = {
        x: p.x + xx * cx + xy * cy + xz * cz,
        y: p.y + yx * cx + yy * cy + yz * cz,
        z: p.z + zx * cx + zy * cy + zz * cz,
      };
      const half = Math.max(
        0.08,
        Math.abs(yx) * (t.halfWidth ?? t.halfHeight) +
          Math.abs(yy) * t.halfHeight +
          Math.abs(yz) * (t.halfLength ?? t.halfHeight),
      );
      const current = this.waterAt(center.x, center.z, Math.min(1.2, t.halfWidth ?? 0.4));
      if (!current) {
        this.restore(entry);
        continue;
      }
      entry.wet = true;
      if (!body.isDynamic()) continue;
      const submerged = clamp((WATER_LEVEL - (center.y - half)) / (2 * half), 0, 1);
      if (!submerged) continue;
      const velocity = body.linvel(),
        mass = body.mass(),
        draft = clamp(t.draft ?? 0.58, 0.2, 0.85);
      const damping = Math.min(1, submerged / draft);
      const upward = clamp((16 * submerged) / draft - velocity.y * 5 * damping, -24, 48);
      body.applyImpulse(
        {
          x: (current.x * 0.18 - velocity.x) * mass * dt * 0.7 * damping,
          y: upward * mass * dt,
          z: (current.z * 0.18 - velocity.z) * mass * dt * 0.7 * damping,
        },
        true,
      );
      const angular = body.angvel(),
        drag = Math.max(0, 1 - dt * damping * 1.3);
      body.setAngvel({ x: angular.x * drag, y: angular.y * drag, z: angular.z * drag }, false);
    }
  }
  snapshot(): Record<string, number> {
    let wood = 0,
      boats = 0;
    for (const entry of this.entries.values())
      if (entry.wet && entry.body?.isValid() && entry.body.isDynamic()) {
        if (entry.wood) wood++;
        else boats++;
      }
    return {
      waterBodies: wood + boats,
      floatingWood: wood,
      floatingBoats: boats,
      waterCandidates: this.entries.size,
      waterRestorations: this.restorations,
    };
  }
}
